import React from 'react';

const PlayerSkinsTable = ({ players, totalSkins }) => {
  // Ensure players is an array and handle various data structures
  const playersArray = React.useMemo(() => {
    // Handle various possible data structures
    if (Array.isArray(players)) {
      return players;
    }
    if (players && typeof players === 'object') {
      // If players is an object, try to extract the leaderboard array
      if (Array.isArray(players.leaderboard)) {
        return players.leaderboard;
      }
      // If it's a single player object, wrap in array
      if (players.golferId || players.id) {
        return [players];
      }
    }
    // Default to empty array
    console.warn('PlayerSkinsTable: players prop is not an array:', typeof players, players);
    return [];
  }, [players]);

  // Sort players by skin percentage (highest first)
  const sortedPlayers = playersArray.sort((a, b) => {
    const aPercentage = a.totalSkinsPercentage || a.skinPercentage || 0;
    const bPercentage = b.totalSkinsPercentage || b.skinPercentage || 0;
    return bPercentage - aPercentage;
  });

  const formatSkinWins = (skinWins) => {
    if (!skinWins || skinWins.length === 0) {
      return 'None';
    }

    return skinWins
      .map(win => `R${win.round}H${win.hole}`)
      .join(', ');
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'TIER_A': return 'tier-a';
      case 'TIER_B': return 'tier-b';
      case 'TIER_C': return 'tier-c';
      case 'TIER_D': return 'tier-d';
      default: return 'tier-default';
    }
  };

  const formatUsername = (fullName) => {
    if (!fullName || fullName === 'N/A') return 'N/A';
    
    const nameParts = fullName.trim().split(' ');
    if (nameParts.length === 1) {
      // If only one name, return as is
      return nameParts[0];
    }
    
    // If multiple parts, take first name + last initial
    const firstName = nameParts[0];
    const lastName = nameParts[nameParts.length - 1];
    const lastInitial = lastName.charAt(0).toUpperCase();
    
    return `${firstName}${lastInitial}`;
  };

  if (!playersArray || playersArray.length === 0) {
    return (
      <div className="player-skins-table">
        <div className="table-header">
          <h4>Player Skins</h4>
        </div>
        <div className="no-players">
          <p>No leaderboard data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="player-skins-table">
      <div className="table-header">
        <h4>Player Skins</h4>
        <span className="player-count">({playersArray.length} players)</span>
      </div>
      
      <div className="table-container">
        <table className="skins-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Skins</th>
              <th>%</th>
              <th>Wins</th>
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => (
              <tr key={player.golferId || player.id}>
                <td className="player-cell">
                  <div className="player-info">
                    <span className="player-name">{player.golferName || player.golfer?.name || player.playerName}</span>
                    <span className={`tier-badge ${getTierColor(player.golferTier || player.golfer?.tier || player.tier || 'TIER_D')}`}>
                      {player.golferTier || player.golfer?.tier || player.tier || 'TIER_D'}
                    </span>
                    {player.userName && (
                      <span className="user-name">({formatUsername(player.userName)})</span>
                    )}
                  </div>
                </td>
                <td className="skins-cell">
                  <span className="skins-count">{player.totalSkins || player.skinsWon || 0}</span>
                </td>
                <td className="percentage-cell">
                  <span className="percentage-value">
                    {Math.round(player.totalSkinsPercentage || player.skinPercentage || 0)}%
                  </span>
                </td>
                <td className="wins-cell">
                  <span className="wins-list">
                    {formatSkinWins(player.skinWins)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlayerSkinsTable;
